import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOverlays } from './overlay-context';
import { Link } from 'react-router-dom';
import { CheckCircle2, ChevronDown } from 'lucide-react';
import { AnimatePresence, m } from 'motion/react';
import { Button } from '../ui/button';
import ControlLink from './ControlLink';
import { MemberAvatar } from './WorkCardMeta';
import { ErrorAlert, LoadingRows } from './page-states';
import { useMembers } from '../wiki/use-member-names';
import { apiFetch, v1Path } from '../../lib/api';
import { formatFriendlyDate } from '../../lib/dates';
import { finishedOnTime, groupByFinishWeek } from '../../lib/done';
import { initiativePath } from '../../lib/initiatives';
import { COMPLETED_TARGET_ATTR } from '../../lib/mark-done';
import { cn } from '../../lib/utils';
import {
  memberDisplayName,
  workItemTypeLabel,
  type ListResponse,
  type MemberDto,
  type ProjectDto,
  type WorkItemSlim,
} from '../../lib/work-types';

const FIRST = 50;
/** Enough to count a busy week when the section starts collapsed. */
const FIRST_COLLAPSED = 50;
const MORE = 20;
const TITLE_CLASS = 'block truncate text-sm font-medium text-foreground no-underline hover:text-primary hover:no-underline';
const LIST_CLASS =
  'divide-y divide-border/70 overflow-hidden rounded-2xl border border-border/80 bg-card shadow-[0_1px_2px_hsl(var(--brand-navy)/0.04),0_4px_12px_-6px_hsl(var(--brand-navy)/0.08)]';

export type DoneScope = {
  /** Only work this person owns. */
  personId?: string | null;
  /** Only this stream's work. */
  projectId?: string;
  /** Only this initiative's launch steps. */
  initiativeId?: string;
};

/** Changes whenever any of the page's own items is edited, so the section can catch up. */
export function changeKey(items: ReadonlyArray<{ id: string; version?: number; state_id?: string }>): string {
  return items.map((item) => `${item.id}.${item.version ?? ''}.${item.state_id ?? ''}`).join('|');
}

function listUrl(scope: DoneScope, offset: number, first: number = FIRST): string {
  const params = new URLSearchParams({
    done: 'true',
    order_by: '-completed_at',
    show_sub_items: 'true',
    limit: String(offset === 0 ? first : MORE),
    offset: String(offset),
  });
  if (scope.personId) params.set('assignee_ids', scope.personId);
  if (scope.initiativeId) params.set('initiative_ids', scope.initiativeId);
  const base = scope.projectId ? v1Path(`/projects/${scope.projectId}/work-items`) : v1Path('/work-items');
  return `${base}?${params.toString()}`;
}

function People({ ids, members }: { ids: string[]; members: Map<string, MemberDto> }) {
  const people = ids.map((id) => members.get(id)).filter((member): member is MemberDto => Boolean(member));
  if (people.length === 0) return <span className="w-10 shrink-0" aria-hidden />;
  return (
    <span className="flex w-10 shrink-0 -space-x-1.5" title={people.map((member) => memberDisplayName(member)).join(', ')}>
      {people.slice(0, 2).map((member) => (
        <MemberAvatar key={member.user_id} member={member} className="ring-2 ring-card" />
      ))}
    </span>
  );
}

function DoneRow({
  item,
  stream,
  members,
  showPeople,
  today,
  onReopen,
}: {
  item: WorkItemSlim;
  stream: string | undefined;
  members: Map<string, MemberDto>;
  showPeople: boolean;
  today: Date;
  onReopen: (item: WorkItemSlim) => void;
}) {
  const onTime = finishedOnTime(item);
  return (
    <m.li
      className="flex items-center gap-3 overflow-hidden px-4 py-3"
      initial={{ opacity: 0, height: 0, paddingTop: 0, paddingBottom: 0, backgroundColor: 'rgba(16, 185, 129, 0.12)' }}
      animate={{ opacity: 1, height: 'auto', paddingTop: 12, paddingBottom: 12, backgroundColor: 'rgba(16, 185, 129, 0)' }}
      transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1], backgroundColor: { delay: 0.3, duration: 0.9 } }}
    >
      <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden />
      <div className="min-w-0 flex-1">
        {item.type === 'initiative' ? (
          <Link to={initiativePath(item.id)} className={TITLE_CLASS}>
            {item.title}
          </Link>
        ) : (
          <ControlLink workItemId={item.id} workItemKey={item.key} projectId={item.project_id} className={TITLE_CLASS}>
            {item.title}
          </ControlLink>
        )}
        <p className="mt-0.5 truncate text-xs text-muted-foreground">
          {[item.type === 'task' ? null : workItemTypeLabel(item.type), stream].filter(Boolean).join(' · ')}
        </p>
      </div>
      {showPeople ? <People ids={item.assignee_ids} members={members} /> : null}
      <span
        className={cn(
          'hidden w-24 shrink-0 text-right text-xs font-medium sm:inline',
          onTime?.late ? 'text-amber-700 dark:text-amber-300' : 'text-emerald-700 dark:text-emerald-300',
        )}
      >
        {onTime?.text ?? ''}
      </span>
      <span className="w-24 shrink-0 text-right text-sm font-medium tabular-nums">
        {item.completed_at ? formatFriendlyDate(new Date(item.completed_at), today) : ''}
      </span>
      <Button type="button" size="sm" variant="outline" onClick={() => onReopen(item)}>
        Reopen
      </Button>
    </m.li>
  );
}

/**
 * A "Done" section at the foot of a page: the latest finished work for a person, stream or
 * initiative, grouped by the week it was done, with more on request.
 */
export function DoneSection({
  scope,
  empty,
  showStream = true,
  showPeople = true,
  refreshKey,
  collapsible = false,
  completedTarget,
}: {
  scope: DoneScope;
  empty: string;
  showStream?: boolean;
  showPeople?: boolean;
  /** Reload when this changes, e.g. `changeKey(items)` of the page's open work. */
  refreshKey?: string;
  /** Start as one "Completed" row that opens to the full list. */
  collapsible?: boolean;
  /** Marked-done work flies here; see `flyToCompleted`. */
  completedTarget?: string;
}) {
  const members = useMembers();
  const { toast } = useOverlays();
  const today = useMemo(() => new Date(), []);
  const [items, setItems] = useState<WorkItemSlim[] | null>(null);
  const [total, setTotal] = useState(0);
  const [streams, setStreams] = useState<Map<string, string>>(() => new Map());
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [open, setOpen] = useState(!collapsible);
  const { personId, projectId, initiativeId } = scope;

  const load = useCallback(
    async (offset: number) => {
      setError(null);
      try {
        const res = await apiFetch<ListResponse<WorkItemSlim>>(
          listUrl({ personId, projectId, initiativeId }, offset, collapsible ? FIRST_COLLAPSED : FIRST),
        );
        setItems((curr) => (offset === 0 ? res.items : [...(curr ?? []), ...res.items]));
        setTotal(res.page.total);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not load finished work');
      }
    },
    [collapsible, initiativeId, personId, projectId],
  );

  useEffect(() => {
    void load(0);
    const onChanged = () => void load(0);
    window.addEventListener('bb:work-items-changed', onChanged);
    return () => window.removeEventListener('bb:work-items-changed', onChanged);
  }, [load, refreshKey]);

  useEffect(() => {
    if (!showStream) return;
    apiFetch<{ items: ProjectDto[] }>(v1Path('/projects?limit=100&offset=0'))
      .then((res) => setStreams(new Map(res.items.map((project) => [project.id, project.name]))))
      .catch(() => undefined);
  }, [showStream]);

  async function reopen(item: WorkItemSlim) {
    try {
      await apiFetch(v1Path(`/projects/${item.project_id}/work-items/${item.id}/unarchive`), {
        method: 'POST',
        body: JSON.stringify({ version: item.version }),
      });
      toast('Reopened');
      window.dispatchEvent(new CustomEvent('bb:work-items-changed'));
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not reopen it');
    }
  }

  const groups = useMemo(() => (items ? groupByFinishWeek(items, today) : []), [items, today]);
  const targetProps = completedTarget ? { [COMPLETED_TARGET_ATTR]: completedTarget } : {};

  return (
    <section aria-label="Completed" className="space-y-3" {...(collapsible ? {} : targetProps)}>
      {collapsible ? (
        <button
          {...targetProps}
          type="button"
          id="done-section-title"
          aria-expanded={open}
          aria-controls="done-section-list"
          onClick={() => setOpen((value) => !value)}
          className="flex w-full items-center gap-3 rounded-2xl border border-border/80 bg-card px-4 py-3 text-left shadow-[0_1px_2px_hsl(var(--brand-navy)/0.04)] transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden />
          <span className="flex-1 text-sm font-semibold">
            Completed
            <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-xs font-medium tabular-nums text-muted-foreground">
              {total}
            </span>
          </span>
          <ChevronDown
            className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200', open && 'rotate-180')}
            aria-hidden
          />
        </button>
      ) : null}

      {open ? (
        <div id="done-section-list" className="space-y-3">
          {error ? <ErrorAlert>{error}</ErrorAlert> : null}
          {!items && !error ? <LoadingRows columns={2} /> : null}
          {items && items.length === 0 ? (
            <p className="rounded-xl bg-muted/50 px-3 py-3 text-sm text-muted-foreground">{empty}</p>
          ) : null}

          {groups.map((group) => (
            <div key={group.id}>
              <h3 className="mb-2 text-xs font-semibold text-muted-foreground">{group.title}</h3>
              <ul className={LIST_CLASS}>
                <AnimatePresence initial={false}>
                  {group.items.map((item) => (
                    <DoneRow
                      key={item.id}
                      item={item}
                      stream={showStream ? streams.get(item.project_id) : undefined}
                      members={members}
                      showPeople={showPeople}
                      today={today}
                      onReopen={(row) => void reopen(row)}
                    />
                  ))}
                </AnimatePresence>
              </ul>
            </div>
          ))}

          {items && items.length < total ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={loadingMore}
              onClick={() => {
                setLoadingMore(true);
                void load(items.length).finally(() => setLoadingMore(false));
              }}
            >
              {loadingMore ? 'Loading…' : 'Show more'}
            </Button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
